import { memo, useEffect, useRef, useState } from "react";
import Icon from "components/Elements/Icon/Icon";
import { useContent } from "contexts/contentContext/contentContext";
import CustomPopup from "../Popups/CustomPopup";
import useOnClickOutside from "hooks/useOnClickOutside";
import useInbox from "hooks/useInbox.ts";
import { useUser } from "contexts/userContext/userContext";
import Button from "../../../Elements/Button/Button";
import SubUsersFilter from "./SubUsersFilter";
import useMediaQueries from "../../../../hooks/useMediaQueries";
import hasAdminAccessRights from "../../../../helpers/hasAdminAccessRights";
import { useAppSelector } from "../../../../store/hooks";
import { selectSearchParams } from "../../../../store/selectors/inboxSelectors";
import { RootState } from "../../../../store/store";
import { SubUserSentMailsT } from "../../../../store/slices/inboxSlice";

const values = ["my", "organization"];
const FIRST_PAGE_NUM = 1;

/**
 * Represents a recipient filter component.
 *
 * This component is responsible for filtering mails based on the recipient
 * and selected sub-user if recipient is an organization.
 */

const RecipientFilter = memo(() => {
  const { getUserData } = useUser();
  const { getTextByKey } = useContent();
  const { desktopCondition } = useMediaQueries();
  const { setSelectedMail, setSearchParams, setPageNumber } = useInbox();
  const searchParams = useAppSelector(selectSearchParams);
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef(null);

  // Local state for selected sub-users and selected sub-user until dropdown is opened
  const [selected, setSelected] = useState<
    RootState["inbox"]["searchParams"]["all"]["rcp"]
  >(["my", "organization"]);
  const [selectedSubUser, setSelectedSubUser] = useState(searchParams?.su);

  useOnClickOutside(ref, () => setShowDropdown(false));

  const toggleShowDropdown = () => setShowDropdown((prev) => !prev);

  useEffect(() => {
    if (!showDropdown) {
      setSelectedSubUser(searchParams?.su);
      setSelected(searchParams?.rcp);
    }
  }, [searchParams?.rcp, showDropdown]);

  useEffect(() => {
    const showRecipientParam = hasAdminAccessRights(
      getUserData()?.isTopUser,
      getUserData()?.subUserRole
    );

    if (showRecipientParam) {
      if (!searchParams?.rcp) {
        if (searchParams?.su) {
          setSearchParams({ rcp: ["organization"], su: searchParams?.su });
          setSelected(["organization"]);
        }
      } else {
        setSelected(searchParams?.rcp);
        setSearchParams({ rcp: searchParams?.rcp });
        if (searchParams.su) {
          setSearchParams({ su: searchParams?.su });
        }
      }
    }
  }, [getUserData()?.UserId]);

  /**
   * Handles the selection of the sub-users.
   */
  const handleSelect = (value: SubUserSentMailsT) => {
    const exists = selected?.includes(value);
    if (exists) {
      if (selected?.length !== 1) {
        setSelected((prev) => prev?.filter((v) => v !== value));
      }
    } else {
      setSelected((prev) => (prev ? [...prev, value] : [value]));
    }
  };

  /**
   * Handles the apply action for the recipient filter.
   * Resets the selected mail, updates the sub user sent mails, and sets the local storage.
   * Toggles the show dropdown state.
   * Updates the selected sub users globally.
   * Sets the query parameters based on the selected sub user.
   */
  const handleApply = () => {
    setSelectedMail(null);
    setSearchParams({
      rcp: selected,
      su: Array.isArray(selectedSubUser) ? selectedSubUser : [selectedSubUser],
    });
    toggleShowDropdown();

    if (!selected?.includes("organization")) {
      setSearchParams({ su: [] });
    } else {
      setSearchParams({
        su: Array.isArray(selectedSubUser)
          ? selectedSubUser
          : [selectedSubUser],
      });
    }

    setSearchParams({
      rcp: selected,
    });

    setPageNumber(FIRST_PAGE_NUM);
  };

  const filterTitleKey =
    searchParams?.rcp!?.length > 1
      ? searchParams?.su?.length === 1
        ? "my-and-sub-user-items"
        : "all-items"
      : searchParams?.rcp?.[0] + "-items";

  return (
    <div className="filter-status-wrapper" ref={ref}>
      <div className="filter-status-button" onClick={toggleShowDropdown}>
        {desktopCondition ? (
          <span>
            {searchParams?.rcp?.length === 1 &&
            searchParams?.rcp?.includes("organization") &&
            searchParams?.su?.length
              ? searchParams.su
              : getTextByKey(filterTitleKey).replace(
                  "%value%",
                  searchParams?.su?.join(",")
                )}
          </span>
        ) : null}
        <Icon
          name={desktopCondition ? "arrow-down" : "newinbox/user-filter"}
          small={desktopCondition}
          large={!desktopCondition}
          gray={desktopCondition}
          darkGray={!desktopCondition}
          showTitle={false}
        />
      </div>
      <CustomPopup showState={showDropdown} topPosition={50}>
        <div className="checkbox-list-dropdown">
          <Icon
            name="x"
            large
            gray
            onClick={() => setShowDropdown(false)}
            iconClass="close-icon"
          />
          <ul>
            {values.map((value, i) => (
              <li
                key={value}
                onClick={() => handleSelect(value as SubUserSentMailsT)}
              >
                <div
                  className={`checkbox-wrapper ${
                    selected?.includes(value as SubUserSentMailsT)
                      ? "selected"
                      : ""
                  }`}
                >
                  {selected?.includes(value as SubUserSentMailsT) && (
                    <Icon
                      name="newInbox/mark"
                      iconClass="mark-icon"
                      blue
                      showTitle={false}
                    />
                  )}
                </div>
                {getTextByKey(value + "-items")}
              </li>
            ))}
          </ul>
          <div className="sub-user-checkbox-list">
            {selected?.includes("organization") ? (
              <SubUsersFilter
                setSelectedSubUser={setSelectedSubUser}
                selectedSubUser={selectedSubUser}
              />
            ) : null}
          </div>
          <div className="buttons">
            <Button onClick={handleApply}>
              {getTextByKey("filter-apply")}
            </Button>
          </div>
        </div>
      </CustomPopup>
    </div>
  );
});

export default RecipientFilter;
